/*
  Очередь к подаче: таблица с выбором счетов и два действия над выбранным.

  Вынесена из `pages/ConveyorPage.jsx` 09.09.2026, когда страница подошла к
  норме в 250 строк. Граница та же, что в скилле `frontend`: страница знает про
  сервер и держит сводные числа, очередь знает про выбор строк и ничего не
  грузит сама - действие ей передают снаружи.

  Состояние выбора живёт здесь, а не на странице: за его пределы оно не выходит
  ни разу, и на странице было бы четвёртым полем состояния, к которому никто не
  обращается.

  Очередь у настоящего портфеля - тысячи счетов, и вся страница вытягивалась бы на
  десятки экранов (осмотр 09.09.2026). С 21.10.2026 страницы режет СЕРВЕР
  (требование владельца: «пагинация серверная»): компонент получает строки текущей
  страницы (`rows`) и свойства панели (`bar`), а откуда они - с сервера или из
  выбора в реестре, - решают обёртки в `ConveyorQueueSource.jsx`. Прежних «загружено
  100, остальные не загружены» больше нет: до любой строки очереди можно долистать.

  Отметки хранятся строками, а не номерами позиций, и переживают смену страницы:
  человек отмечает первую страницу, листает, отмечает вторую - в волну уходят обе.
  Сумма выбранного считается по самим отмеченным строкам, потому что чужих страниц
  в браузере нет.
*/

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Check, Confirm, Tag } from './Common.jsx'
import AssemblyClosedNote from './AssemblyClosedNote.jsx'
import CourtField from './CourtField.jsx'
import PageBar from './PageBar.jsx'
import PickShown from './PickShown.jsx'
import PickWhole from './PickWhole.jsx'
import { accounts as accountsWord, claims, date, money } from '../api/format.js'

export default function ConveyorQueue({
  rows: visible,
  bar,
  sections,
  sectionsError,
  canAct,
  assemble = { allowed: true, closedByTier: false },
  busy,
  onAction,
  limit,
  preset = [],
  fetchAll,
}) {
  // `preset` - строки, отмеченные заранее: их выбрали в реестре и нажали «В
  // конвейер» (16.10.2026). Человек пришёл сюда собрать волну именно из них.
  const [chosen, setChosen] = useState(preset)
  const [court, setCourt] = useState('')
  // «Отметить поданными» необратимо (нет действия «снять отметку»): сначала вопрос.
  const [askFiled, setAskFiled] = useState(false)

  const chosenIds = chosen.map((row) => row.account_id)
  const shownIds = visible.map((row) => row.account_id)
  const allChosen = visible.length > 0 && shownIds.every((id) => chosenIds.includes(id))
  const someChosen = shownIds.some((id) => chosenIds.includes(id))
  // Отмеченные могут лежать на других страницах (заранее из реестра или на прежней
  // странице): итог выбора говорит, сколько отмеченных сейчас не видно.
  const elsewhere = chosenIds.filter((id) => !shownIds.includes(id)).length
  const chosenSum = chosen.reduce((sum, row) => sum + Number(row.claim_amount), 0)
  // Предел называет сервер (`filing_limit`). Выбрать можно больше (до тысячи из реестра), но
  // действие над таким числом отказывает, поэтому кнопки гаснут с объяснением (аудит 28.10.2026).
  const tooMany = Boolean(limit) && chosen.length > limit

  const toggle = (row) =>
    setChosen((current) =>
      current.some((item) => item.account_id === row.account_id)
        ? current.filter((item) => item.account_id !== row.account_id)
        : [...current, row],
    )

  /*
    🔴 «Выбрать все» берёт ровно то, что сейчас на экране, - решение владельца
    09.09.2026. До него кнопка брала всю полученную очередь, включая строки под
    свёрнутым списком: человек видел двадцать отмеченных строк, а в волну уходило
    сто. Прежние отметки при этом сохраняются - переход на другую страницу и
    повторное нажатие добавляют новые строки к уже выбранным, а не начинают
    отбор заново. С 21.10.2026 «на экране» - это страница панели, и отметки
    между страницами сохраняются по-прежнему.
  */
  const toggleAll = () =>
    setChosen((current) =>
      allChosen
        ? current.filter((row) => !shownIds.includes(row.account_id))
        : [...current, ...visible.filter((row) => !current.some((i) => i.account_id === row.account_id))],
    )

  // «Выбрать весь отбор» (решение владельца 23.10.2026): вся очередь с сервера добавляется к
  // выбору; строки несут сумму, поэтому итог «на N ₽» верен и для непоказанных страниц.
  const addAll = (rows) =>
    setChosen((current) => [
      ...current,
      ...rows.filter((row) => !current.some((item) => item.account_id === row.account_id)),
    ])

  async function run(kind) {
    const done = await onAction(kind, chosenIds, court)
    setAskFiled(false)
    if (done) setChosen([])
  }

  return (
    <>
      {canAct && fetchAll ? (
        <PickWhole
          total={bar.total}
          shown={visible.length}
          filterKey="queue"
          fetchAll={fetchAll}
          onPicked={addAll}
          selected={chosen.length}
        />
      ) : null}
      <div className="card">
        <PickShown
          count={visible.length}
          picked={shownIds.filter((id) => chosenIds.includes(id)).length}
          onToggle={toggleAll}
        />
        <div className="table-scroll">
          <table className="table table--fixed table--conveyor">
            <thead>
              <tr>
                <th className="table__control">
                  <Check
                    checked={allChosen}
                    indeterminate={someChosen && !allChosen}
                    onChange={toggleAll}
                    title="Выбрать показанные счета"
                  />
                </th>
                <th>Счёт</th>
                <th>Адрес</th>
                <th className="table__num">К взысканию</th>
                <th className="table__num">Пошлина</th>
                <th>Отметки</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.account_id}>
                  <td className="table__control">
                    <Check
                      checked={chosenIds.includes(row.account_id)}
                      onChange={() => toggle(row)}
                      title={`Выбрать счёт ${row.number}`}
                    />
                  </td>
                  <td>
                    <Link to={`/accounts/${row.account_id}`}>№ {row.number}</Link>
                  </td>
                  <td data-label="Адрес">{row.address}</td>
                  <td className="table__num" data-label="К взысканию">
                    {money(row.claim_amount)}
                  </td>
                  <td className="table__num" data-label="Пошлина">
                    {money(row.state_fee)}
                  </td>
                  <td data-label="Отметки">
                    {row.is_burning ? (
                      <Tag kind="burn">
                        горит{row.burning_date ? ` до ${date(row.burning_date)}` : ''}
                      </Tag>
                    ) : null}
                    {row.claims > 1 ? <Tag kind="muted">{claims(row.claims)}</Tag> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PageBar {...bar} what="счетов" />
      </div>

      {canAct && assemble.closedByTier ? <AssemblyClosedNote /> : null}
      {canAct ? (
        <>
          {/*
            Итог выбора словами и суммой: по одним подсвеченным флажкам среди
            сорока семи строк не видно, сколько счетов набрано и на сколько
            денег, а от этого зависит и пошлина, и объём волны.
          */}
          <p className="muted">
            {chosen.length === 0
              ? 'Ни один счёт не выбран: отметьте те, что идут в эту волну.'
              : `Выбрано ${accountsWord(chosen.length)} на ${money(chosenSum)}${
                  elsewhere ? `, из них на других страницах: ${elsewhere}` : ''
                }.`}
            {tooMany ? ` За раз можно не больше ${limit}: снимите часть отметок.` : ''}
          </p>

          <div className="row">
            {/*
              Участок выбирается из заведённых, но НЕ подставляется сам
              (14.09.2026): волна собирается из счетов разных домов, у неё нет
              одного «предполагаемого» участка. Подставить сюда любой из них -
              значит свести всю волну в один реестр и в один суд.
            */}
            {/* Реестр волны - сборка документов: ярус «Под ключ» её закрывает (строка 127),
                а «Отметить поданными» остаётся: это отметка о действии, не сборка. */}
            {assemble.allowed ? (
              <>
                <CourtField
                  label="Судебный участок для реестра волны"
                  value={court}
                  onChange={setCourt}
                  sections={sections}
                  error={sectionsError}
                  placeholder="пусто - по каждому участку отдельно"
                />
                <button
                  type="button"
                  className="button"
                  disabled={busy || chosen.length === 0 || tooMany}
                  onClick={() => run('registry')}
                >
                  Собрать реестр на {chosen.length}
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="button button--quiet"
              disabled={busy || chosen.length === 0 || tooMany}
              onClick={() => setAskFiled(true)}
            >
              Отметить поданными
            </button>
          </div>
          {askFiled ? (
            <Confirm
              title="Отметить поданными"
              action={`Отметить: ${accountsWord(chosen.length)}`}
              onConfirm={() => run('filed')}
              onCancel={() => setAskFiled(false)}
              busy={busy}
            >
              Вы отмечаете, что заявления по {accountsWord(chosen.length)} на {money(chosenSum)}{' '}
              поданы в суд. Это отметка о вашем действии, снять её потом нельзя. Если заявления ещё
              не поданы, нажмите «Отмена».
            </Confirm>
          ) : null}
          {/*
            11.09.2026, вместе со справочником участков: реестр волны стал
            собираться по каждому участку отдельно, потому что юрист несёт пачку
            в каждый участок свою. Заполненное поле возвращает прежнее поведение
            - один реестр на всю волну.
          */}
          {assemble.allowed ? (
            <p className="muted">
              Оставьте поле пустым - реестр соберётся по каждому участку отдельно, по адресам
              помещений.
            </p>
          ) : null}
        </>
      ) : null}
    </>
  )
}
